import { motion } from 'framer-motion';
import { useState } from 'react';
import { MediaGallery } from '../../../components/MediaGallery';

export function ServicesSection() {
  const [selectedService, setSelectedService] = useState<string | null>(null);

  const tourPackages = [
    {
      icon: 'ri-sun-line',
      title: '동부 코스',
      subtitle: '일출과 자연의 경이',
      description: '성산일출봉, 섭지코지, 우도, 만장굴 등 제주 동부의 숨은 명소를 로컬 전문가가 안내합니다',
      price: '₩150,000~',
      color: 'from-[#FF8E53] to-[#ff7a3d]',
      highlights: ['성산일출봉', '섭지코지', '우도', '만장굴']
    },
    {
      icon: 'ri-sunset-line',
      title: '서부 코스',
      subtitle: '노을과 감성의 여행',
      description: '협재해수욕장, 한림공원, 오설록, 카멜리아힐 등 제주 서부의 낭만을 경험하세요',
      price: '₩150,000~',
      color: 'from-[#0E7FC0] to-[#0a5f92]',
      highlights: ['협재해수욕장', '한림공원', '오설록', '카멜리아힐']
    },
    {
      icon: 'ri-mountain-line',
      title: '한라산 코스',
      subtitle: '정상 정복의 감동',
      description: '백록담 등반부터 하산 후 온천까지, 안전하고 완벽한 한라산 등반 투어',
      price: '₩180,000~',
      color: 'from-[#4A7C59] to-[#3a6347]',
      highlights: ['백록담 등반', '전문 가이드', '온천', '등산 장비']
    },
    {
      icon: 'ri-restaurant-line',
      title: '맛집 코스',
      subtitle: '진짜 제주의 맛',
      description: '로컬만 아는 숨은 맛집과 제주 전통 음식을 맛보는 미식 투어',
      price: '₩120,000~',
      color: 'from-[#FF1493] to-[#ff0080]',
      highlights: ['흑돼지', '해산물', '로컬 맛집', '전통 시장']
    }
  ];

  const customServices = [
    {
      icon: 'ri-user-heart-line',
      title: '1:1 맞춤 설계',
      description: '당신의 취향과 일정에 맞춘 완벽한 여행 플랜'
    },
    {
      icon: 'ri-map-pin-user-line',
      title: '로컬 큐레이션',
      description: '관광지가 아닌, 진짜 제주를 경험하는 특별한 코스'
    },
    {
      icon: 'ri-time-line',
      title: '유연한 일정',
      description: '여행 중에도 자유롭게 조정 가능한 일정'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '친구같은 서비스',
      description: '여행 전부터 후까지 세심하게 케어'
    }
  ];

  const handleServiceClick = (serviceTitle: string) => {
    setSelectedService(serviceTitle);
  };

  const handleCloseGallery = () => {
    setSelectedService(null);
  };

  return (
    <>
      <section id="services" className="py-20 sm:py-24 md:py-28 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8">
          {/* Header */}
          <div className="text-center mb-16 sm:mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-3 bg-[#0E7FC0]/10 px-6 py-3 rounded-full mb-6 sm:mb-8"
            >
              <i className="ri-map-2-line text-[#0E7FC0] text-lg sm:text-xl"></i>
              <span className="text-[#0E7FC0] text-base sm:text-lg font-medium">투어 패키지</span>
            </motion.div>
            
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#2C2C2C] mb-6 sm:mb-8 leading-tight px-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              제주를 제대로<br />
              경험하는 방법
            </motion.h2>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-lg sm:text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4"
            >
              로컬 전문가가 직접 큐레이션한<br />
              4가지 프리미엄 투어 코스
            </motion.p>
          </div>

          {/* Tour Packages Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-24">
            {tourPackages.map((tour, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group cursor-pointer"
                onClick={() => handleServiceClick(tour.title)}
              >
                <div className={`bg-gradient-to-br ${tour.color} rounded-3xl p-8 sm:p-10 text-white h-full shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2`}>
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <i className={`${tour.icon} text-4xl`}></i>
                    </div>
                    <div className="text-right">
                      <div className="text-sm opacity-80 mb-1">시작가</div>
                      <div className="text-2xl font-bold">{tour.price}</div>
                    </div>
                  </div>
                  
                  <h3 className="text-3xl font-bold mb-2">{tour.title}</h3>
                  <p className="text-lg text-white/90 mb-4">{tour.subtitle}</p>
                  <p className="text-base text-white/80 leading-relaxed mb-6">{tour.description}</p>
                  
                  <div className="flex flex-wrap gap-2 mb-6">
                    {tour.highlights.map((highlight, idx) => (
                      <span key={idx} className="bg-white/20 px-3 py-1 rounded-full text-sm">
                        {highlight}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center gap-2 text-white/90 group-hover:gap-4 transition-all">
                    <span className="font-medium">자세히 보기</span>
                    <i className="ri-arrow-right-line text-xl"></i>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Custom Travel Section */}
          <div className="bg-gradient-to-br from-[#2C2C2C] to-[#1a1a1a] rounded-3xl p-10 sm:p-16 text-white mb-16">
            <div className="text-center mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="inline-flex items-center gap-3 bg-white/10 px-6 py-3 rounded-full mb-6"
              >
                <i className="ri-vip-crown-line text-[#FF8E53] text-xl"></i>
                <span className="text-white text-lg font-medium">맞춤형 여행</span>
              </motion.div>
              
              <h3 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                당신만을 위한<br />특별한 제주
              </h3>
              <p className="text-xl text-white/80 max-w-2xl mx-auto">
                정해진 코스가 아닌, 당신의 이야기로 만드는 여행
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {customServices.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/15 transition-all"
                >
                  <div className="w-12 h-12 bg-[#FF8E53] rounded-xl flex items-center justify-center mb-4">
                    <i className={`${service.icon} text-2xl text-white`}></i>
                  </div>
                  <h4 className="text-xl font-bold mb-2">{service.title}</h4>
                  <p className="text-white/70 text-sm leading-relaxed">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Premium Services */}
          <div className="text-center mb-12">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl sm:text-4xl font-bold text-[#2C2C2C] mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              프리미엄 서비스
            </motion.h3>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-lg text-gray-600"
            >
              특별한 순간을 더욱 특별하게
            </motion.p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 mb-16">
            {[
              { icon: 'ri-camera-line', title: '웨딩 스냅', color: 'from-rose-400 to-rose-600' },
              { icon: 'ri-film-line', title: 'CF & 방송 촬영', color: 'from-purple-500 to-purple-700' },
              { icon: 'ri-team-line', title: '워크숍 이동', color: 'from-teal-500 to-teal-700' },
              { icon: 'ri-basketball-line', title: '골프 부킹', color: 'from-green-600 to-green-800' },
              { icon: 'ri-walk-line', title: '올레길 투어', color: 'from-emerald-500 to-emerald-700' },
              { icon: 'ri-heart-pulse-line', title: '결혼식 픽업', color: 'from-red-500 to-red-700' },
              { icon: 'ri-taxi-line', title: '택시 투어', color: 'from-[#FF1493] to-[#ff0080]' },
              { icon: 'ri-car-line', title: '벤츠 스프린터', color: 'from-[#2C2C2C] to-[#1a1a1a]' }
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="group cursor-pointer"
                onClick={() => handleServiceClick(service.title)}
              >
                <div className={`bg-gradient-to-br ${service.color} rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-1`}>
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform mx-auto">
                    <i className={`${service.icon} text-2xl`}></i>
                  </div>
                  <h4 className="text-lg font-bold text-center">{service.title}</h4>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="bg-gradient-to-r from-[#FF8E53] to-[#ff7a3d] rounded-3xl p-10 sm:p-16 text-white">
              <h3 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-6">지금 바로 상담받으세요!</h3>
              <p className="text-lg sm:text-xl md:text-2xl mb-8 sm:mb-10 opacity-90">
                로컬 전문가가 당신만의 특별한 제주 여행을 설계해드립니다
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-4 sm:gap-6">
                <a href="tel:010-5685-7818" className="bg-white text-[#FF8E53] px-8 sm:px-10 py-4 sm:py-5 rounded-full text-lg sm:text-xl font-bold hover:bg-gray-100 transition-all flex items-center justify-center gap-3 whitespace-nowrap shadow-xl hover:scale-105">
                  <i className="ri-phone-line text-xl"></i>
                  010-5685-7818
                </a>
                <button 
                  onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
                  className="bg-white/20 backdrop-blur-md text-white px-8 sm:px-10 py-4 sm:py-5 rounded-full text-lg sm:text-xl font-bold hover:bg-white/30 transition-all border-2 border-white/30 flex items-center justify-center gap-3 whitespace-nowrap shadow-xl hover:scale-105"
                >
                  <i className="ri-message-3-line text-xl"></i>
                  실시간 상담
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Media Gallery Modal */}
      {selectedService && (
        <MediaGallery
          serviceTitle={selectedService}
          onClose={handleCloseGallery}
        />
      )}
    </>
  );
}
